import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { Building2, Check, ChevronDown, LogOut, Menu, Search, UserRound } from 'lucide-react';
import { useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useTenant } from '../tenant/TenantProvider.tsx';
import { ContextHelp } from '../help/ContextHelp.tsx';
import { useGuard } from '../performance/unsaved.tsx';

const menuItem =
  'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-slate-700 outline-none data-[highlighted]:bg-slate-100';

export function Header({ onOpenMenu, menuButtonRef }: { onOpenMenu: () => void; menuButtonRef?: RefObject<HTMLButtonElement | null> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const guard = useGuard();
  const { logout } = useAuth();
  const { me, context, tenantId, switchTenant, can } = useTenant();
  const [search, setSearch] = useState('');
  const others = me.memberships.filter((m) => m.status === 'ACTIVE');
  const logo = context.tenant.theme.logo_url;

  return (
    <header className="flex h-16 shrink-0 items-center gap-1 border-b border-slate-200/80 bg-white px-2 text-slate-700 sm:h-[72px] sm:gap-2 sm:px-6 xl:px-8">
      <button ref={menuButtonRef} className="rounded-lg p-2 hover:bg-slate-100 md:hidden" onClick={onOpenMenu} aria-label={t('header.openMenu')} aria-haspopup="dialog">
        <Menu size={20} />
      </button>
      <div className="flex shrink-0 items-center rounded-lg bg-white px-1 py-1">
        <img src={logo || '/logo-produit.svg'} alt={context.tenant.name} className={`h-7 w-auto ${others.length > 1 ? 'max-w-12' : 'max-w-20'} object-contain sm:h-9 sm:max-w-28`} />
      </div>

      {others.length > 1 ? (
        <Dropdown.Root>
          <Dropdown.Trigger
            className="flex h-10 w-10 shrink-0 items-center justify-center gap-0.5 rounded-lg px-1 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:h-auto sm:w-auto sm:min-w-0 sm:max-w-64 sm:shrink sm:justify-start sm:gap-1.5 sm:px-2 sm:py-2"
            aria-label={`${context.tenant.name} — ${t('header.switchTenant')}`}
            data-testid="tenant-switcher"
          >
            <Building2 size={16} className="shrink-0" />
            <span className="hidden truncate sm:block">{context.tenant.name}</span>
            <ChevronDown size={12} className="shrink-0" />
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content align="start" sideOffset={6} className="z-50 min-w-60 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
              <Dropdown.Label className="px-2.5 py-1.5 text-xs font-semibold uppercase text-slate-500">{t('header.switchTenant')}</Dropdown.Label>
              {others.map((m) => (
                <Dropdown.Item key={m.tenant_id} className={menuItem} onSelect={() => { if (m.tenant_id !== tenantId) guard(() => switchTenant(m.tenant_id)); }}>
                  <span className="flex-1">
                    {m.tenant_name}
                    <span className="block text-xs text-slate-500">{m.role_name}</span>
                  </span>
                  {m.tenant_id === tenantId ? <Check size={16} className="text-brand" /> : null}
                </Dropdown.Item>
              ))}
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      ) : (
        <span className="hidden truncate px-2 text-sm font-semibold text-slate-700 sm:block">{context.tenant.name}</span>
      )}

      <div className="flex-1" />
      {can('accounts.read') ? <form className="mx-2 hidden w-64 xl:block" role="search" onSubmit={(event) => { event.preventDefault(); const q = search.trim(); if (q) { navigate(`/comptes?${new URLSearchParams({ q })}`, { flushSync: true }); setSearch(''); } }}>
        <label className="relative block">
          <span className="sr-only">{t('workspace.searchAccounts')}</span>
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            maxLength={100}
            placeholder={t('workspace.searchAccounts')}
            data-testid="header-account-search"
            className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50/80 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </label>
      </form> : null}

      <ContextHelp />

      <Dropdown.Root>
        <Dropdown.Trigger className="ml-1 flex shrink-0 items-center gap-2 rounded-lg border-l border-slate-100 px-2 py-1 hover:bg-slate-50" aria-label={`${me.full_name} — ${t('header.userMenu')}`} data-testid="user-menu">
          <span aria-hidden="true" className="grid h-9 w-9 place-items-center rounded-full bg-brand/10 text-xs font-bold text-brand"><UserRound size={18} /></span>
          <span className="hidden max-w-36 truncate text-sm font-medium sm:block" data-testid="user-name">{me.full_name}</span>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end" sideOffset={6} className="z-50 w-60 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
            <div className="px-2.5 py-2">
              <p className="text-sm font-semibold text-slate-900">{me.full_name}</p>
              <p className="text-xs text-slate-500">{context.role.name}</p>
            </div>
            <Dropdown.Separator className="my-1 h-px bg-slate-100" />
            <Dropdown.Item className={menuItem} onSelect={() => navigate('/compte')}>
              <UserRound size={16} /> {t('nav.myAccount')}
            </Dropdown.Item>
            <Dropdown.Item className={`${menuItem} text-red-700`} onSelect={() => guard(() => void logout())}>
              <LogOut size={16} /> {t('header.logout')}
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </header>
  );
}
